import moment from "moment";
import { useMemo, useState } from "react";
import { Cards } from "@/components/profile/statistics/cards";
import { Ca } from "@/components/profile/statistics/ca";
import { Maps } from "@/components/profile/statistics/maps";
import { Filters } from "@/components/profile/statistics/filters";
import { useGetCompanyUsersBo } from "@/api/user.api";
import { useGetExpeditionStatsBo } from "@/api/expedition.api";

export interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

const getDateRange = (
  timeScale: "year" | "month" | "week",
  year: number,
  month: number,
  week: number,
  isPrev?: boolean,
): DateRange => {
  let startDate: moment.Moment;
  let endDate: moment.Moment;

  if (isPrev) {
    if (timeScale === "year") {
      year -= 1;
    } else if (timeScale === "month") {
      month -= 1;
      if (month < 0) {
        month = 11;
        year -= 1;
      }
    } else if (timeScale === "week") {
      week -= 1;
      if (week <= 0) {
        week = 52;
        year -= 1;
      }
    }
  }

  switch (timeScale) {
    case "year":
      startDate = moment().year(year).startOf("year"); // 1st Jan of the year
      endDate = moment().year(year).endOf("year"); // 31st Dec of the year
      break;
    case "month":
      startDate = moment().year(year).month(month).startOf("month"); // 1st day of the month
      endDate = moment().year(year).month(month).endOf("month"); // Last day of the month
      break;
    case "week":
      startDate = moment().year(year).week(week).startOf("isoWeek"); // Monday of the week
      endDate = moment().year(year).week(week).endOf("isoWeek"); // Sunday of the week
      break;
    default:
      throw new Error("Invalid time scale");
  }

  return { startDate, endDate };
};

type StatisticsProps = {
  companyId: string;
};

export const Statistics = ({ companyId }: StatisticsProps) => {
  const [timeScale, setTimeScale] = useState<"year" | "month" | "week">("year");
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number>(new Date().getMonth());
  const [week, setWeek] = useState<number>(moment().week());
  const [source, setSource] = useState<"all" | "cotations" | "customCotations">("all");
  const [userId, setUserId] = useState<string | undefined>();
  const [billingEntityId, setBillingEntityId] = useState<string | undefined>();

  const selectedPeriod = getDateRange(timeScale, year, month, week);
  const prevPeriod = getDateRange(timeScale, year, month, week, true);

  const { data: expeditionStats } = useGetExpeditionStatsBo({
    id: companyId,
    payload: {
      processStatus: ["VALIDATED"],
      finalizedOrPickupDateAfter: selectedPeriod.startDate.toISOString(),
      finalizedOrPickupDateBefore: selectedPeriod.endDate.toISOString(),
      quotationType:
        source === "cotations"
          ? "QUOTATION"
          : source === "customCotations"
            ? "CUSTOM_QUOTATION"
            : undefined,
      createdById: userId === "undefined" ? undefined : userId,
      billingEntityId: billingEntityId === "undefined" ? undefined : billingEntityId,
    },
  });
  const { data: previousExpeditionStats } = useGetExpeditionStatsBo({
    id: companyId,
    payload: {
      processStatus: ["VALIDATED"],
      finalizedOrPickupDateAfter: prevPeriod.startDate.toISOString(),
      finalizedOrPickupDateBefore: prevPeriod.endDate.toISOString(),
      quotationType:
        source === "cotations"
          ? "QUOTATION"
          : source === "customCotations"
            ? "CUSTOM_QUOTATION"
            : undefined,
      createdById: userId === "undefined" ? undefined : userId,
      billingEntityId: billingEntityId === "undefined" ? undefined : billingEntityId,
    },
  });

  const filteredExpeditionStats = useMemo(() => {
    return expeditionStats?.data?.filter((item) => {
      const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
      return moment(refDate).isBetween(selectedPeriod.startDate, selectedPeriod.endDate);
    });
  }, [expeditionStats, selectedPeriod]);

  const filteredPreviousExpeditionStats = useMemo(() => {
    return previousExpeditionStats?.data?.filter((item) => {
      const refDate = moment.max(moment(item.finalizedAt), moment(item.pickupInfo?.pickupDate));
      return moment(refDate).isBetween(prevPeriod.startDate, prevPeriod.endDate);
    });
  }, [previousExpeditionStats, prevPeriod]);

  const { data: users } = useGetCompanyUsersBo(companyId);

  return (
    <div className="flex w-full max-w-[1280px] flex-col gap-8 self-center">
      <Filters
        setTimeScale={setTimeScale}
        timeScale={timeScale}
        setYear={setYear}
        year={year}
        setMonth={setMonth}
        month={month}
        setWeek={setWeek}
        week={week}
        setSource={setSource}
        source={source}
        setUserId={setUserId}
        users={users || []}
        setBillingEntityId={setBillingEntityId}
        billingEntities={[]}
      />
      <Cards
        expeditionStats={filteredExpeditionStats}
        previousExpeditionStats={filteredPreviousExpeditionStats}
        timeScale={timeScale}
      />
      <Ca
        expeditionStats={filteredExpeditionStats}
        timeScale={timeScale}
        startDate={selectedPeriod.startDate}
        endDate={selectedPeriod.endDate}
      />
      <Maps selectedPeriod={filteredExpeditionStats} />
    </div>
  );
};
